/**
 * ITArchitecture — project archive.
 *
 * Image URLs, names, descriptions, ids and VR links are carried over verbatim
 * from the previous `src/assets/projects/projects.js`.
 *
 * Three fields were added so the redesigned index and detail pages can show a
 * proper metadata table:
 *
 *   `slug`      — a human-readable URL segment. Numeric ids still resolve, so
 *                 existing links such as /project/10 keep working.
 *   `typology`  — building type, read off the project's own name.
 *   `location`  — only filled in where the project name states the place.
 *                 Deliberately `null` otherwise rather than guessed; the UI
 *                 omits the row when it is missing.
 *   `year`      — only filled in where the name states the year. `null`
 *                 elsewhere, for the same reason.
 *
 * @typedef {Object} ApartmentFloorplan
 * @property {number} id
 * @property {string} name
 * @property {string} floorplan Absolute URL of the floorplan image.
 *
 * @typedef {Object} FilmSource
 * @property {string} src  Root-relative path under public/.
 * @property {string} type MIME type, so the browser can pick a format.
 *
 * @typedef {Object} ProjectFilm
 * @property {FilmSource[]} sources  Offered best-format-first.
 * @property {string} posterUrl      Still shown before playback starts.
 * @property {number} width          Intrinsic pixel width of the encode.
 * @property {number} height         Intrinsic pixel height of the encode.
 *
 * @typedef {Object} ProjectPreview
 * @property {FilmSource[]} sources  Silent, seamless, roughly two seconds.
 *
 * @typedef {Object} Project
 * @property {number} id
 * @property {string} slug
 * @property {string} name
 * @property {string} description
 * @property {string} typology
 * @property {?string} location
 * @property {?number} year
 * @property {"landscape"|"portrait"} [renderShape] Shape of this project's
 *                                  renders, which decides the gallery
 *                                  rhythm. Defaults to "landscape".
 * @property {string[]} images      `images[0]` is the picture shown behind the
 *                                  project title; the rest form the gallery.
 *                                  A single entry therefore means "cover
 *                                  only, no gallery", which is how a film-led
 *                                  project is expressed.
 * @property {string} cover         Master under /media/covers.
 * @property {string} coverOriginal The studio's untouched render, on S3 or
 *                                  under /media/projects.
 * @property {boolean} hasVR
 * @property {string} [vrLink]
 * @property {ApartmentFloorplan[]} [apartments]
 * @property {ProjectFilm} [film]   Walkthrough animation, where one exists.
 * @property {ProjectPreview} [preview] Short living-render loop for the card
 *                                  and the page header. See `buildPreview`.
 */

const S3 = "https://ita-architecture-images.s3.eu-west-2.amazonaws.com";

/**
 * Where project media that is NOT in the S3 bucket lives, relative to the
 * site root.
 *
 * Every project up to and including `basic` was uploaded to S3 before this
 * site was built, so its renders are referenced there. The two most recent
 * projects arrived from the studio as plain files, and there is no S3 upload
 * path set up from this repository — so they are committed under
 * public/media/projects/<slug>/ instead.
 *
 * Nothing downstream has to care which of the two it is getting. `SmartImage`
 * puts both through Netlify Image CDN, and a local path actually needs less
 * configuration than S3 does: files under public/ are transformable out of the
 * box, whereas the bucket had to be allow-listed in netlify.toml.
 */
const PROJECT_MEDIA_DIRECTORY = "/media/projects";

/* -------------------------------------------------------------- image sets */

const neviteraKompleksImages = [
  `${S3}/nevitera-kompleks/0e0e8803-6d87-491f-b89b-38ad7475c0f7.jpg`,
  `${S3}/nevitera-kompleks/4c231d71-fe32-4f75-95ad-79417eef3a81.jpg`,
  `${S3}/nevitera-kompleks/9f37c049-0811-45fe-98f0-9f1dd6c60c07.jpg`,
  `${S3}/nevitera-kompleks/14bad2dd-3944-46ff-a426-f0e2010e67c6.jpg`,
  `${S3}/nevitera-kompleks/20eb5391-abaf-445e-9620-f310e77e3b9c.jpg`,
  `${S3}/nevitera-kompleks/28b5da13-c123-478a-a6cc-43be9a8ddf4e.jpg`,
  `${S3}/nevitera-kompleks/281a0ae3-9c06-48d4-a33b-2a1b964a086f.jpg`,
  `${S3}/nevitera-kompleks/416ec400-1c44-4356-8dcd-5ba66052bbed.jpg`,
  `${S3}/nevitera-kompleks/448c175d-3c8a-4437-8737-edf9f0621c62.jpg`,
  `${S3}/nevitera-kompleks/456b81b8-887e-4763-bb62-bac4e2242b89.jpg`,
  `${S3}/nevitera-kompleks/8953ca47-f988-4b73-84e2-5aa1070aa8d0.jpg`,
  `${S3}/nevitera-kompleks/76833570-6bce-4ba2-aea2-0da0b9f00700.jpg`,
  `${S3}/nevitera-kompleks/a305d7df-3a32-4467-a684-21f4cef725aa.jpg`,
  `${S3}/nevitera-kompleks/abbbe9d0-3680-4824-818d-5a984e4af448.jpg`,
  `${S3}/nevitera-kompleks/ca561ded-6a20-4812-83d7-c817b3325f8b.jpg`,
  `${S3}/nevitera-kompleks/d0e5d314-ea19-4566-a711-7d8274143e8a.jpg`,
  `${S3}/nevitera-kompleks/d04adf05-4a59-40d1-a660-81403187fc66.jpg`,
  `${S3}/nevitera-kompleks/dd408b42-9349-4c4e-b5f8-9cc86f5a5b51.jpg`,
  `${S3}/nevitera-kompleks/e4d33e8c-5ee4-40da-8f23-f2a2ec1854f9.jpg`,
  `${S3}/nevitera-kompleks/e4e35a1d-2612-4b80-b99e-47713e3612fb.jpg`,
  `${S3}/nevitera-kompleks/e809543e-1f95-453a-9a40-525e312bc771.jpg`,
  `${S3}/nevitera-kompleks/f6b88e99-7363-4f42-9851-d8eb8c1e7faa.jpg`,
  `${S3}/nevitera-kompleks/f814988a-ed19-42c5-8f19-e981972952ee.jpg`,
  `${S3}/nevitera-kompleks/fde85ecd-8cb0-4356-8df0-80f3c948730b.jpg`,
];

const stambeniSrebrenik1Images = [
  `${S3}/stambeni-srebrenik-1/d5c223b6-657a-46e2-9d41-480584601a47.jpg`,
  `${S3}/stambeni-srebrenik-1/a8231dbf-7b2a-4598-ba29-cbbab91586cf.jpg`,
  `${S3}/stambeni-srebrenik-1/dcc1da2c-bcf4-4af6-80fb-f798af2a180d.jpg`,
  `${S3}/stambeni-srebrenik-1/28f04c8f-18a3-49a0-a079-db3e147624fb.jpg`,
  `${S3}/stambeni-srebrenik-1/2376fb6f-f313-42bf-a2b9-ad0d5b40a35a.jpg`,
  `${S3}/stambeni-srebrenik-1/802924c3-2e05-4846-ac26-aa564d2e0277.jpg`,
];

const trespaImages = [
  `${S3}/trespa/c43e6b50-0419-49d0-9b5e-0a7a6d00280e.jpg`,
  `${S3}/trespa/40b6a831-e7d4-4c8b-bd60-3a6eebbc04f9.jpg`,
  `${S3}/trespa/94e6d54c-842d-4852-b46d-ebfb9a7e1c0e.jpg`,
  `${S3}/trespa/a49b2656-9a52-4d8b-96bf-f6bf7b04b33e.jpg`,
  `${S3}/trespa/b13d255e-727f-4017-9bb9-95213d00ffb7.jpg`,
  `${S3}/trespa/c18eda7f-0a7c-46ff-8b02-54a683475e4f.jpg`,
  `${S3}/trespa/27276bac-7923-4ada-9d42-c067ffd4c77c.jpg`,
  `${S3}/trespa/d85fd411-c5bf-422d-bd46-a7ecdded9732.jpg`,
  `${S3}/trespa/1ad86476-c96e-4e90-8295-5f8d15219cb4.jpg`,
  `${S3}/trespa/2a73cdf0-d314-413d-99f8-5e042c50319d.jpg`,
  `${S3}/trespa/319c4910-b8e1-494d-9c77-c3568d16bcee.jpg`,
  `${S3}/trespa/86788f7f-5653-4034-9099-7ef8f91bd11c.jpg`,
  `${S3}/trespa/6491704e-4a62-4630-81d8-593491efedb9.jpg`,
];

const stambeniSrebrenik2Images = [
  `${S3}/stambeni-srebrenik-2/9be5be39-5fe7-4ed1-9fb2-fb5029f7f733.jpg`,
  `${S3}/stambeni-srebrenik-2/7cb51a5a-3fad-4434-aacf-c90357279e84.jpg`,
  `${S3}/stambeni-srebrenik-2/431cd8db-7f28-44de-a3bd-7578935134e7.jpg`,
  `${S3}/stambeni-srebrenik-2/b11eef72-e5a0-4f56-9672-51d49f82f191.jpg`,
  `${S3}/stambeni-srebrenik-2/e5e85a3e-fcd5-473b-99b1-2887b6c20ed9.jpg`,
  `${S3}/stambeni-srebrenik-2/ec051df2-b252-4ff9-940f-b9a8a1f61bad.jpg`,
  `${S3}/stambeni-srebrenik-2/002f5662-b1f1-4184-b9d1-701e499517b0.jpg`,
  `${S3}/stambeni-srebrenik-2/2c94c2ec-c566-4ecb-afa2-925fd9acf8cc.jpg`,
  `${S3}/stambeni-srebrenik-2/5fdf7ff9-e081-493b-a744-8168372a304c.jpg`,
];

const hmSrebrenikImages = [
  `${S3}/hm-srebrenik/efdb255b-238e-4685-b741-935c3b0671bd.jpg`,
  `${S3}/hm-srebrenik/6b2dcf76-c4e0-4306-9aca-3115355085f8.jpg`,
  `${S3}/hm-srebrenik/429a19ca-481d-4927-8075-18155c9b0e05.jpg`,
  `${S3}/hm-srebrenik/ab689fc5-c0aa-4e4e-885f-76de3d4b88a7.jpg`,
  `${S3}/hm-srebrenik/aec9cfda-15e9-49b5-87aa-199c6c68fd05.jpg`,
];

const sjedicImages = [
  `${S3}/sjedic/bc0b8865-48ca-4ea2-9580-be6d4cfd5b38.jpg`,
  `${S3}/sjedic/07fc10c8-0e71-47dd-bd4d-27de57f92c6a.jpg`,
  `${S3}/sjedic/7cc28355-223b-477a-938c-2c9b3e59cb06.jpg`,
  `${S3}/sjedic/9d0196c8-790e-4149-91d3-b46437a7ad91.jpg`,
  `${S3}/sjedic/8937cc05-6c9f-461c-a736-b2ed0f1daaa8.jpg`,
  `${S3}/sjedic/a1ed4df8-e418-48f7-a90c-e54685741c75.jpg`,
  `${S3}/sjedic/a53b0f4d-12e4-48ad-9426-8402692b18d2.jpg`,
  `${S3}/sjedic/a74d8c88-d4a7-45b5-a070-4645178b9dcd.jpg`,
  `${S3}/sjedic/da9d508c-ea26-498e-814b-6ebd224b912f.jpg`,
];

const neum2024Images = [
  `${S3}/neum-2024/1aa9a3dd-1813-4353-afc7-5bc9f8235296.jpg`,
  `${S3}/neum-2024/f38e1bce-60c2-4853-b29f-ab4a9558c94a.jpg`,
  `${S3}/neum-2024/ec21376a-18dc-499e-a54b-e5e3e836f2f2.jpg`,
  `${S3}/neum-2024/f8b39962-84c5-451b-b6e2-bda9a3d23407.jpg`,
  `${S3}/neum-2024/9ca0b9fb-cbb1-4c1c-8f27-3e3b74eaee5b.jpg`,
  `${S3}/neum-2024/84a954da-4d7a-40c2-8045-2a37f88f9528.jpg`,
  `${S3}/neum-2024/589d907c-2750-42b8-8192-90328efb9b45.jpg`,
  `${S3}/neum-2024/1083f84a-59e8-4a66-b193-4833ba805925.jpg`,
  `${S3}/neum-2024/7148d98e-8cf4-45eb-baa1-4415350b37b6.jpg`,
  `${S3}/neum-2024/00813204-43f3-4450-8773-b7373be12c9a.jpg`,
];

const salkicImages = [
  `${S3}/salkic/e8307aef-67d5-46c3-8de7-75578dea9c4d.jpg`,
  `${S3}/salkic/d70540b1-4bab-42f4-b4c3-72ffa1a16821.jpg`,
  `${S3}/salkic/cde566fa-0cdf-49e0-9a4b-90ff68a7a534.jpg`,
  `${S3}/salkic/5d998e0f-38dc-4439-92fd-088d34a50a17.jpg`,
  `${S3}/salkic/be59a825-bcfb-4141-8aa2-36e5e5a52728.jpg`,
  `${S3}/salkic/31c447c6-fc09-45d5-8b2b-7fbe6d6e7beb.jpg`,
  `${S3}/salkic/97e8a4e4-62fc-491a-89c1-869613a7a03b.jpg`,
];

const basicImages = [
  `${S3}/basic/81557795_2620914151297115_5296900410726940672_n.jpg`,
  `${S3}/basic/216702471_4060336787354837_8021865870240253587_n.jpg`,
  `${S3}/basic/218003541_4060336410688208_8448395128753904487_n.jpg`,
  `${S3}/basic/233563632_4138541219534393_2912693831777957479_n.jpg`,
  `${S3}/basic/233868584_4138541456201036_4463520358886940918_n.jpg`,
  `${S3}/basic/234619599_4138541759534339_7802455480582076894_n.jpg`,
  `${S3}/basic/235646122_4138541102867738_9141948095166536067_n.jpg`,
  `${S3}/basic/317964837_5574199602635207_2012409464563729883_n.jpg`,
  `${S3}/basic/318002545_5574200039301830_7531477191086156137_n.jpg`,
  `${S3}/basic/318063445_5574199755968525_271212904688641189_n.jpg`,
  `${S3}/basic/318079397_5574199282635239_4949113945550054426_n.jpg`,
  `${S3}/basic/318102611_5574199455968555_8440406520699110422_n.jpg`,
  `${S3}/basic/318215688_5574200479301786_4413406810812973450_n.jpg`,
  `${S3}/basic/318325034_5574198865968614_5004852101964389390_n.jpg`,
  `${S3}/basic/318447847_5574198659301968_4427776842161044567_n.jpg`,
  `${S3}/basic/81924124_2620914117963785_2680259441827250176_n.jpg`,
  `${S3}/basic/82010994_2620914181297112_8222863747463512064_n.jpg`,
];

/*
  Mars renders.

  Five of these six were exported from the virtual tour with its navigation
  hotspots still switched on, so they carry white ellipse outlines near the
  bottom edge — clearly visible artefacts, not part of the architecture.
  `b00808a3…` is the only clean frame, so it leads the sequence and is used as
  the cover: the cover appears full-bleed behind the project title and as the
  card thumbnail, where a stray marker is most obvious.

  The real fix is to re-export the other five from the tour with hotspots
  hidden, then restore whatever order reads best.
*/
const marsImages = [
  `${S3}/mars/b00808a3-54a2-4b64-8d9d-0661948948e5.jpg`, // clean
  `${S3}/mars/80a79884-ed2a-4bdd-8a5c-9ab9c0e3e0a7.jpg`, // hotspots bottom L+R
  `${S3}/mars/4c54ca01-2145-40c0-95e0-11ce346c6d20.jpg`, // hotspots bottom L+R
  `${S3}/mars/5949341c-3061-459e-b742-eaacc04cf739.jpg`, // hotspot bottom L
  `${S3}/mars/822787d4-f9a6-4ecd-9bf1-83cc73bdf25b.jpg`, // hotspot bottom R
  `${S3}/mars/eba29489-9329-4c7a-ae26-a0d2bbc20552.jpg`, // hotspots bottom L+R
];

/* -------------------------------------------- local-media projects (2026) */

const resadDirectory = `${PROJECT_MEDIA_DIRECTORY}/resad-stambeni-objekat`;

const resadImages = [
  `${resadDirectory}/01-bazen-i-fasada.jpg`,
  `${resadDirectory}/02-terasa-uz-bazen.jpg`,
  `${resadDirectory}/03-vrtna-fasada.jpg`,
  `${resadDirectory}/04-prilaz-i-vrt.jpg`,
  `${resadDirectory}/05-natkrivena-terasa.jpg`,
];

/*
  Walkthrough animation for the Rešad house.

  Encoded from the studio's clip at its own resolution — 848 x 478, which is
  small. The file that reached this repository had already been through
  WhatsApp's compression, and nothing can put back detail that was thrown away
  there, so upscaling it would only make the softness larger.

  Two consequences, both deliberate:

    - `width` and `height` are carried on the record so the player can be
      capped at the encode's native size rather than stretched across the
      viewport. An 848px film blown up to a 1440px column looks broken; the same
      film at 848px looks fine.
    - If the studio's original export turns up, replace film.mp4 / film.webm /
      film-poster.jpg and raise these two numbers. Nothing else changes.

  The poster is the film's own first frame, matching the hero's approach, so
  there is no visible jump when playback starts.
*/
const resadFilm = {
  sources: [
    { src: `${resadDirectory}/film.webm`, type: "video/webm" },
    { src: `${resadDirectory}/film.mp4`, type: "video/mp4" },
  ],
  posterUrl: `${resadDirectory}/film-poster.jpg`,
  width: 848,
  height: 478,
};

const jahorinaDirectory = `${PROJECT_MEDIA_DIRECTORY}/aparthotel-jahorina`;

/*
  Both Jahorina renders are upright — 1044 x 1024 and 953 x 1051 — which is
  why this record carries `renderShape: "portrait"`. The detail page's default
  gallery opens with a 16:9 full-width frame, and frames crop to their aspect
  ratio, so an upright picture would lose roughly half its height there: the
  top of the building, which is the whole point of this facade.

  The two renders are the same building in summer and in winter. Summer leads,
  because it is the larger file and shows the hexagonal screen clearly at the
  small sizes the archive card and hover preview use; the snow render has more
  atmosphere but turns to mush as a thumbnail.
*/
const jahorinaImages = [
  `${jahorinaDirectory}/01-ljetni-pogled.jpg`,
  `${jahorinaDirectory}/02-zimski-pogled.jpg`,
];

const jahorinaFilm = {
  sources: [
    { src: `${jahorinaDirectory}/film.webm`, type: "video/webm" },
    { src: `${jahorinaDirectory}/film.mp4`, type: "video/mp4" },
  ],
  posterUrl: `${jahorinaDirectory}/film-poster.jpg`,
  width: 848,
  height: 538,
};

const visnjaDirectory = `${PROJECT_MEDIA_DIRECTORY}/visnja-bijeljina`;

/*
  Višnja is the archive's first film-led project: the studio sent a 10-second
  flythrough and no still renders at all.

  `images` therefore holds exactly one entry, the film's opening frame — the
  only moment that shows the whole building and its signage. The detail page
  reads a single-entry `images` as "no gallery": it drops the gallery section,
  the "Galerija — N vizualizacija" button and the Vizualizacije row, and leads
  with the film instead. That one frame is still needed, because it is what
  appears behind the project title, on the archive card and in the index
  hover preview.

  It is an 848px frame doing a job the other covers do at 2048px or more, so
  it is the softest cover in the archive. The right fix is still renders from
  the studio; failing that, the upscale route in public/media/covers/README.md
  applies here as much as it did to the 960px covers.
*/
const visnjaImages = [`${visnjaDirectory}/01-ulicna-fasada.jpg`];

const visnjaFilm = {
  sources: [
    { src: `${visnjaDirectory}/film.webm`, type: "video/webm" },
    { src: `${visnjaDirectory}/film.mp4`, type: "video/mp4" },
  ],
  // The cover still IS the film's first frame, so it is reused rather than
  // written out a second time: the picture cannot drift out of sync.
  posterUrl: visnjaImages[0],
  width: 848,
  height: 552,
};

const majevicaDirectory = `${PROJECT_MEDIA_DIRECTORY}/majevica-stambeni-objekat`;

const majevicaImages = [
  `${majevicaDirectory}/01-bazen-i-fasada.jpg`,
  `${majevicaDirectory}/02-vrt-i-bazen.jpg`,
  `${majevicaDirectory}/03-terase-odozgo.jpg`,
];

/** Floorplans for the Mars building. */
const marsApartments = Array.from({ length: 10 }, (_, index) => ({
  id: index + 1,
  name: `Stan ${index + 1}`,
  floorplan: `${S3}/mars/tlocrt-stan-${index + 1}.png`,
}));

/* ---------------------------------------------------------------- previews */

/**
 * Projects whose card and page header play a short living-render loop.
 *
 * Only the ones on the landing page. The archive shows all fourteen, and
 * fourteen loops on one page would be several megabytes of video for a
 * visitor who is scrolling past most of them — on a site where the AVIF
 * quality was deliberately dropped from 72 to 50 to save fifty kilobytes a
 * cover. `LiveCover` only fetches what comes near the viewport, so the cost
 * is bounded either way, but there is no reason to author the rest until
 * they earn it.
 *
 * What the loops are, deliberately: the render, alive. A slow push-in, water
 * moving, planting stirring — not the hero film's drawing-fills-in treatment
 * in miniature. Two reasons. At card size a sketch becoming a render is
 * illegible, it just reads as a flicker. And the drawing is the hero's one
 * trick; spending it seven more times on the same page would make it
 * wallpaper.
 *
 * Six are cut from footage that already existed — the five hero shots, taken
 * from their tails where the shot has resolved into the studio's own render
 * and is only drifting, plus the Jahorina and Višnja walkthroughs. Only
 * Majevica needed anything new.
 *
 * Each is silent, about two seconds, and seamless: the clip starts half a
 * second in and its tail is cross-faded back into that opening, so it begins
 * and ends on the same frame. Without that a drift loop visibly snaps back.
 *
 * To regenerate, see public/media/projects/README.md.
 */
const SLUGS_WITH_PREVIEW = [
  "mars-otoka-sarajevo",
  "resad-stambeni-objekat",
  "aparthotel-jahorina",
  "visnja-bijeljina",
  "majevica-stambeni-objekat",
  "nevitera-turisticki-kompleks-brcko",
  "trespa-stambeni-objekat",
];

/**
 * @param {string} slug
 * @returns {ProjectPreview}
 */
function buildPreview(slug) {
  return {
    sources: [
      {
        src: `${PROJECT_MEDIA_DIRECTORY}/${slug}/preview.mp4`,
        type: "video/mp4",
      },
    ],
  };
}

/* ----------------------------------------------------------------- archive */

/**
 * Where the upscaled cover masters live, relative to the site root.
 *
 * A cover is shown full-bleed behind the project title, as the archive
 * thumbnail and as the hover preview — so it is the one image per project
 * that has to be sharp. Several of the studio's originals were small
 * (`stambeni-srebrenik-1` and `salkic` are 960x540, `basic` is 1241x560), and
 * the browser was stretching them two to three times on a high-density
 * screen, which read as soft.
 *
 * Each cover was therefore upscaled to 4K and written out as a 2560px JPEG
 * master under public/, which does two things: the deploy CDN serves them
 * instead of a single S3 region in London, and Netlify Image CDN resizes and
 * re-encodes them to AVIF per breakpoint (see src/lib/imageSource.js).
 *
 * The upscaler sharpens rather than redraws — the architecture is unchanged.
 * Gallery images are still the studio's untouched originals on S3; only the
 * covers are processed. `coverOriginal` on each record keeps a pointer to the
 * untouched source.
 *
 * The two 2026 projects need none of that: their renders arrived at 2048px,
 * which is already past the point where the browser has to stretch anything,
 * so their covers are straight copies of the lead render rather than upscales.
 * Nothing in the code distinguishes the two cases — the path is still derived
 * from the slug.
 *
 * To regenerate, see public/media/covers/README.md.
 */
const COVER_DIRECTORY = "/media/covers";

/** @type {Project[]} */
const projectRecords = [
  {
    id: 10,
    slug: "mars-otoka-sarajevo",
    name: "Mars Otoka Sarajevo",
    description:
      "Stvaramo jedinstvene arhitektonske projekte koji spajaju estetiku, " +
      "funkcionalnost i savremen dizajn, prilagođene vašim željama i potrebama.",
    typology: "Stambeno-poslovni objekat",
    location: "Sarajevo, Otoka",
    year: null,
    images: marsImages,
    coverOriginal:marsImages[0],
    hasVR: true,
    vrLink: "https://mars.ita-architecture.com/",
    apartments: marsApartments,
  },
  {
    id: 11,
    slug: "resad-stambeni-objekat",
    name: "Rešad Stambeni Objekat",
    description:
      "Projektujemo porodične kuće u prizemlju, s otvorenim rasporedom, " +
      "prirodnim materijalima i direktnom vezom dnevnog prostora s vrtom i " +
      "bazenom.",
    typology: "Stambeni objekat",
    location: null,
    year: null,
    images: resadImages,
    coverOriginal: resadImages[0],
    hasVR: false,
    film: resadFilm,
  },
  {
    id: 14,
    slug: "aparthotel-jahorina",
    name: "Aparthotel Jahorina",
    description:
      "Projektujemo turističke objekte koji odgovaraju planinskom kontekstu " +
      "— s prepoznatljivom saćastom fasadom, apartmanima na spratovima i " +
      "otvorenim prizemljem okrenutim prema stazi.",
    typology: "Aparthotel",
    location: "Jahorina",
    year: null,
    renderShape: "portrait",
    images: jahorinaImages,
    coverOriginal: jahorinaImages[0],
    hasVR: false,
    film: jahorinaFilm,
  },
  {
    id: 13,
    slug: "visnja-bijeljina",
    name: "Višnja Bijeljina",
    description:
      "Projektujemo stambeno-poslovne objekte koji spajaju poslovne " +
      "prostore u prizemlju, stanove na spratovima i prepoznatljiv ulični " +
      "karakter.",
    typology: "Stambeno-poslovni objekat",
    location: "Bijeljina",
    year: null,
    images: visnjaImages,
    coverOriginal: visnjaImages[0],
    hasVR: false,
    film: visnjaFilm,
  },
  {
    id: 12,
    slug: "majevica-stambeni-objekat",
    name: "Majevica Stambeni Objekat",
    description:
      "Kreiramo kuće koje prate nagib terena — s terasama u nivoima, " +
      "kamenim podzidima i velikim staklenim površinama okrenutim prema " +
      "pogledu.",
    typology: "Stambeni objekat",
    location: "Majevica",
    year: null,
    images: majevicaImages,
    coverOriginal: majevicaImages[0],
    hasVR: false,
  },
  {
    id: 1,
    slug: "nevitera-turisticki-kompleks-brcko",
    name: "Nevitera Turistički Komplex Brčko",
    description:
      "Projektujemo arhitektonske komplekse koji objedinjuju estetiku, " +
      "funkcionalnost i skladan raspored, stvarajući prostore prilagođene " +
      "potrebama savremenih zajednica.",
    typology: "Turistički kompleks",
    location: "Brčko",
    year: null,
    images: neviteraKompleksImages,
    coverOriginal:neviteraKompleksImages[0],
    hasVR: false,
  },
  {
    id: 3,
    slug: "trespa-stambeni-objekat",
    name: "Trespa Stambeni Objekat",
    description:
      "Stvaramo jedinstvene arhitektonske projekte koji spajaju estetiku, " +
      "funkcionalnost i savremen dizajn, prilagođene vašim željama i potrebama.",
    typology: "Stambeni objekat",
    location: null,
    year: null,
    images: trespaImages,
    coverOriginal:trespaImages[0],
    hasVR: true,
    vrLink: "https://trespa.ita-architecture.com/",
  },
  {
    id: 4,
    slug: "neum-apartmani-2024",
    name: "Neum Apartmani 2024",
    description:
      "Stvaramo jedinstvene arhitektonske projekte koji spajaju estetiku, " +
      "funkcionalnost i savremen dizajn, prilagođene vašim željama i potrebama.",
    typology: "Apartmani",
    location: "Neum",
    year: 2024,
    images: neum2024Images,
    coverOriginal:neum2024Images[0],
    hasVR: false,
  },
  {
    id: 2,
    slug: "stambeni-objekat-srebrenik-ii",
    name: "Stambeni Objekat Srebrenik II",
    description:
      "Projektujemo stambene objekte i kuće koje spajaju moderan dizajn, " +
      "funkcionalnost i toplinu doma, prilagođene vašem stilu života i " +
      "potrebama porodice.",
    typology: "Stambeni objekat",
    location: "Srebrenik",
    year: null,
    images: stambeniSrebrenik2Images,
    coverOriginal:stambeniSrebrenik2Images[0],
    hasVR: false,
  },
  {
    id: 7,
    slug: "hm-zgrada-srebrenik",
    name: "HM Zgrada Srebrenik",
    description:
      "Projektujemo stambene zgrade koje kombinuju savremeni dizajn, " +
      "funkcionalnost i kvalitetan životni prostor, prilagođene potrebama " +
      "modernih zajednica.",
    typology: "Stambena zgrada",
    location: "Srebrenik",
    year: null,
    images: hmSrebrenikImages,
    coverOriginal:hmSrebrenikImages[0],
    hasVR: false,
  },
  {
    id: 6,
    slug: "sjedic-stambeni-odzak",
    name: "Sjedić Stambeni Odžak",
    description:
      "Stvaramo jedinstvene arhitektonske projekte koji spajaju estetiku, " +
      "funkcionalnost i savremen dizajn, prilagođene vašim željama i potrebama.",
    typology: "Stambeni objekat",
    location: "Odžak",
    year: null,
    images: sjedicImages,
    coverOriginal:sjedicImages[0],
    hasVR: false,
  },
  {
    id: 5,
    slug: "stambeni-objekat-srebrenik-i",
    name: "Stambeni Objekat Srebrenik I",
    description:
      "Kreiramo kuće i stambene objekte koji odišu udobnošću, estetikom i " +
      "praktičnošću, oblikovane prema vašim željama i svakodnevnim potrebama.",
    typology: "Stambeni objekat",
    location: "Srebrenik",
    year: null,
    images: stambeniSrebrenik1Images,
    coverOriginal:stambeniSrebrenik1Images[0],
    hasVR: false,
  },
  {
    id: 8,
    slug: "salkic-stambeni-objekat",
    name: "Salkić Stambeni Objekat",
    description:
      "Projektujemo stambene zgrade koje kombinuju savremeni dizajn, " +
      "funkcionalnost i kvalitetan životni prostor, prilagođene potrebama " +
      "modernih zajednica.",
    typology: "Stambeni objekat",
    location: null,
    year: null,
    images: salkicImages,
    coverOriginal:salkicImages[0],
    hasVR: false,
  },
  {
    id: 9,
    slug: "basic-stambeni-objekat",
    name: "Bašić Stambeni Objekat",
    description:
      "Projektujemo stambene zgrade koje kombinuju savremeni dizajn, " +
      "funkcionalnost i kvalitetan životni prostor, prilagođene potrebama " +
      "modernih zajednica.",
    typology: "Stambeni objekat",
    location: null,
    year: null,
    images: basicImages,
    coverOriginal:basicImages[0],
    hasVR: false,
  },
];

/**
 * The archive, with each record's `cover` pointed at its master and, where
 * one is authored, its `preview` loop.
 *
 * Both paths are derived from the slug rather than written out per record,
 * so adding a project means dropping `<slug>.jpg` into public/media/covers
 * and nothing else — and giving an existing project a preview means dropping
 * `preview.mp4` into its media folder and adding its slug to
 * `SLUGS_WITH_PREVIEW`.
 *
 * @type {Project[]}
 */
const projects = projectRecords.map((project) => ({
  ...project,
  cover: `${COVER_DIRECTORY}/${project.slug}.jpg`,
  ...(SLUGS_WITH_PREVIEW.includes(project.slug)
    ? { preview: buildPreview(project.slug) }
    : {}),
}));

/**
 * Resolve a project from a URL parameter.
 *
 * Accepts either the new slug ("mars-otoka-sarajevo") or the old numeric id
 * ("10"), so links shared before the redesign continue to resolve.
 *
 * @param {string | undefined} routeParameter Value of the :projectId segment.
 * @returns {Project | undefined}
 */
export function findProjectByRouteParameter(routeParameter) {
  if (!routeParameter) return undefined;

  const bySlug = projects.find((project) => project.slug === routeParameter);
  if (bySlug) return bySlug;

  const numericId = Number(routeParameter);
  if (!Number.isNaN(numericId)) {
    return projects.find((project) => project.id === numericId);
  }
  return undefined;
}

/**
 * The project that follows the given one in the archive, wrapping around at
 * the end so the detail page always has a "next project" link.
 *
 * @param {Project} currentProject
 * @returns {Project}
 */
export function findNextProject(currentProject) {
  const currentIndex = projects.findIndex(
    (project) => project.id === currentProject.id
  );
  return projects[(currentIndex + 1) % projects.length];
}

/** The project used for the full-bleed feature section on the home page. */
export const featuredProject = projects[0];

export default projects;
